import { useEffect, useRef, useState } from 'react';
import { Graph } from '@antv/x6';
import { registerNativeNodes, GradientSpecs } from '../shapes/nativeNodes';
import { registerProceduralEdges } from '../edges/procedural';
import { registerStructuralEdges } from '../edges/structural';
import { registerCustomRouters } from '../utils/registerRouters';

/** 注册所有自定义节点形状和连线类型（仅执行一次） */
let initialized = false;
function ensureRegistered() {
  if (initialized) return;
  initialized = true;

  // 使用原生 SVG 节点，避免 Safari foreignObject 渲染偏移 bug
  registerNativeNodes();

  registerProceduralEdges();
  registerCustomRouters();
  registerStructuralEdges();
}

export function useGraphInit() {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<Graph | null>(null);
  const [graph, setGraph] = useState<Graph | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    console.log('[useGraphInit] useEffect triggered, container:', !!container);
    if (!container) {
      console.warn('[useGraphInit] Container ref is null, aborting');
      return;
    }

    // Safari 可能在首次渲染时容器尺寸为 0，需要等待容器就绪后再初始化
    let g: Graph | null = null;
    let resizeTimer: number | undefined;
    let graphInitialized = false;

    const initGraph = () => {
      if (graphInitialized) return false;

      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      console.log('[useGraphInit] Checking container size:', {
        width,
        height,
        clientWidth: container.clientWidth,
        clientHeight: container.clientHeight,
      });

      if (width === 0 || height === 0) {
        console.warn(
          '[useGraphInit] Container has zero dimensions, waiting...'
        );
        return false;
      }

      ensureRegistered();

      const DRAG_THRESHOLD = 5;

      console.log('[useGraphInit] Creating Graph with size:', {
        width,
        height,
      });

      g = new Graph({
        container,
        width,
        height,
        connecting: {
          allowMulti: false,
        },
        panning: { enabled: true, modifiers: ['alt'] },
        mousewheel: { enabled: true, modifiers: [], zoomAtMousePosition: true },
        grid: {
          visible: true,
          type: 'dot',
          args: {
            color: '#e0e0e0',
            thickness: 1,
          },
        },
        background: {
          color: '#ffffff',
        },
        clickThreshold: DRAG_THRESHOLD,
        interacting: {
          nodeMovable: false,
          edgeMovable: true,
          edgeLabelMovable: true,
        },
      });

      Object.values(GradientSpecs).forEach(spec => g!.defineGradient(spec));
      graphInitialized = true;
      graphRef.current = g;
      (window as any).__OPM_GRAPH__ = g;

      console.log('[useGraphInit] Graph created, scheduling resize check');


      // 初始化后调整画布尺寸
      requestAnimationFrame(() => {
        if (g && containerRef.current) {
          const { width: w, height: h } =
            containerRef.current.getBoundingClientRect();
          if (w > 0 && h > 0) {
            g.resize(w, h);
          }
        }
        setGraph(g);
      });

      return true;
    };

    // 监听容器尺寸变化
    const resizeObserver = new ResizeObserver(entries => {
      console.log(
        '[useGraphInit] ResizeObserver fired, entries:',
        entries.length
      );
      if (resizeTimer) clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(() => {
        if (!graphInitialized) {
          console.log(
            '[useGraphInit] Attempting deferred initialization via ResizeObserver'
          );
          if (initGraph()) {
            console.log('[useGraphInit] Graph initialized after resize');
          }
          return;
        }

        if (g && containerRef.current) {
          const { width, height } =
            containerRef.current.getBoundingClientRect();
          console.log('[useGraphInit] ResizeObserver updating graph size:', {
            width,
            height,
          });
          if (width > 0 && height > 0) {
            g.resize(width, height);
          }
        }
      }, 50);
    });
    resizeObserver.observe(container);

    if (!initGraph()) {
      console.log(
        '[useGraphInit] Container not ready yet, waiting for ResizeObserver...'
      );
    }

    return () => {
      console.log('[useGraphInit] Cleanup called');
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeObserver.disconnect();
      if (g) {
        (window as any).__OPM_GRAPH__ = null;
        g.dispose();
      }
      graphRef.current = null;
      setGraph(null);
    };
  }, []);

  return { containerRef, graphRef, graph };
}
